import { sendMessage } from "utils/webview";
import { trackCapiStandardEvent } from "./metaCapi";

/**
 * 인앱(WebView) 가입의 Meta 이벤트 지연 전송.
 *
 * 인앱 가입 시점에는 유입 채널을 알 수 없다 — 온보딩 `acquisition/1` 에서 사용자가 답한 뒤에야 안다.
 * 다른 플랫폼 광고로 들어온 가입이 Meta 데이터세트를 오염시키지 않도록,
 * 가입 시점에는 "보류" 만 남기고 유입 채널이 **인스타그램**일 때만 Meta 가입 이벤트 2종을 보낸다.
 *   - 네이티브 FB SDK: `send_fb_event` Login
 *   - Meta Pixel + CAPI: `CompleteRegistration`
 * Firebase `sign_up`·Appsflyer `af_complete_registration` 은 이 모듈과 무관하게 가입 즉시 나간다 (useLogin).
 *
 * 보류 플래그는 userId 를 값으로 localStorage 에 둔다 — 로그인과 유입 채널 응답 사이에 WebView 가
 * 재시작돼도 살아남고, 같은 기기의 다른 계정이 답해도 소비되지 않는다.
 * 유입 채널 응답 없이 이탈하면 영구 미전송 (의도된 동작).
 */
const PENDING_META_SIGNUP_KEY = "pending_meta_signup";

/** 이 값으로 답한 가입만 Meta 로 보낸다 (Acquisition_1 의 선택지 라벨과 동일해야 함). */
export const META_SIGNUP_CHANNEL = "인스타그램";

export function markPendingMetaSignup(userId: number | string): void {
  try {
    localStorage.setItem(PENDING_META_SIGNUP_KEY, String(userId));
  } catch {}
}

function consumePendingMetaSignup(userId: number | string): boolean {
  try {
    if (localStorage.getItem(PENDING_META_SIGNUP_KEY) !== String(userId)) {
      return false;
    }
    localStorage.removeItem(PENDING_META_SIGNUP_KEY);
    return true;
  } catch {
    return false;
  }
}

export function sendMetaSignupEvents(user: {
  userId: number | string;
  email?: string;
}): void {
  sendMessage(
    JSON.stringify({
      event: "send_fb_event",
      params: {
        eventName: "Login",
      },
    })
  );

  trackCapiStandardEvent(
    "CompleteRegistration",
    {
      value: 0,
      currency: "KRW",
      externalId: user.userId,
      email: user.email,
    },
    undefined,
    undefined,
    true
  );
}

/**
 * 유입 채널 제출 성공 시 호출. 보류 중인 가입이면 플래그를 소비하고,
 * 채널이 인스타그램일 때만 Meta 가입 이벤트를 보낸다. 전송했으면 true.
 */
export function resolvePendingMetaSignup(
  channelName: string,
  user: { userId?: number | string; email?: string } | null | undefined
): boolean {
  if (user?.userId === undefined || user.userId === null) return false;
  if (!consumePendingMetaSignup(user.userId)) return false;
  if (channelName !== META_SIGNUP_CHANNEL) return false;
  sendMetaSignupEvents({ userId: user.userId, email: user.email });
  return true;
}
